import type { Contract } from '../contractTypes';
import type { SectionDef } from '../../../components/ui/sectionCard/types';
import type { ContractSectionKey } from '../config/contractTokens';

/**
 * Section definitions for the Contract detail panel.
 * Keys reference the 'contract' i18n namespace (no contractDetail. prefix).
 *
 * Fields without a value are always rendered (label visible, value empty) —
 * no hideWhenEmpty / hideEmptyFields flags, per spec §2️⃣.
 *
 * Column layout:
 *   column 1 — contractInfo, contractDocs, contractEnd
 *   column 2 — position, remuneration, seniority, leave, funding, manager, comment
 */
export const CONTRACT_SECTIONS: SectionDef<Contract, ContractSectionKey>[] = [
  // ── Column 1 ──────────────────────────────────────────────────────────────

  {
    id: 'contract_info',
    group: 'contract',
    column: 1,
    // Always open and non-foldable: the primary block stays expanded with no
    // chevron, while every other block starts folded (same as the employee
    // detail's "personal" section).
    defaultExpanded: true,
    collapsible: false,
    titleKey: 'sections.contractInfo',
    fields: [
      { key: 'contract_number',         labelKey: 'fields.contractNumber',        type: 'text' },
      { key: 'contract_creation_type',  labelKey: 'fields.contractCreationType',  type: 'labeled-code' },
      { key: 'contract_current',        labelKey: 'fields.contractCurrent',       type: 'labeled-code', translatePrefix: 'employeeTables.filterOptions.yesNo' },
      { key: 'contract_type',           labelKey: 'fields.contractType',          type: 'labeled-code' },
      { key: 'initial_start_date',      labelKey: 'fields.initialStartDate',      type: 'date' },
      { key: 'contract_start_date',     labelKey: 'fields.contractStartDate',     type: 'date' },
      { key: 'contract_end_date',       labelKey: 'fields.contractEndDate',       type: 'date' },
      { key: 'work_location',           labelKey: 'fields.workLocation',          type: 'text' },
      { key: 'trial_period',            labelKey: 'fields.trialPeriod',           type: 'labeled-code', translatePrefix: 'employeeTables.filterOptions.yesNo' },
      { key: 'trial_period_end_date',   labelKey: 'fields.trialPeriodEndDate',    type: 'date' },
    ],
  },

  {
    id: 'contract_docs',
    group: 'contract',
    column: 1,
    titleKey: 'sections.contractDocs',
    fields: [
      { key: 'contract_file', labelKey: 'fields.contractFile', type: 'text', fullWidth: true },
      { key: 'job_profile',   labelKey: 'fields.jobProfile',   type: 'labeled-code', fullWidth: true, translatePrefix: 'employeeTables.filterOptions.yesNo' },
      { key: 'profile_file',  labelKey: 'fields.profileFile',  type: 'text', fullWidth: true },
    ],
  },

  {
    id: 'contract_end',
    group: 'contract',
    column: 1,
    titleKey: 'sections.contractEnd',
    fields: [
      { key: 'termination_type',     labelKey: 'fields.terminationType',    type: 'labeled-code' },
      { key: 'termination_reason',   labelKey: 'fields.terminationReason',  type: 'text' },
      { key: 'final_settlement',     labelKey: 'fields.finalSettlement',    type: 'labeled-code', translatePrefix: 'employeeTables.filterOptions.yesNo' },
      { key: 'indemnity_start_date', labelKey: 'fields.indemnityStartDate', type: 'date' },
    ],
  },

  // ── Column 2 ──────────────────────────────────────────────────────────────

  {
    id: 'position',
    group: 'contract',
    column: 2,
    titleKey: 'sections.position',
    fields: [
      { key: 'function_grid',  labelKey: 'fields.functionGrid',  type: 'labeled-code' },
      { key: 'job_function',   labelKey: 'fields.jobFunction',   type: 'labeled-code' },
      { key: 'level',          labelKey: 'fields.level',          type: 'text' },
      { key: 'category',       labelKey: 'fields.category',       type: 'labeled-code' },
      { key: 'position_grade', labelKey: 'fields.positionGrade',  type: 'labeled-code' },
    ],
  },

  {
    id: 'remuneration',
    group: 'contract',
    column: 2,
    titleKey: 'sections.remuneration',
    fields: [
      { key: 'employer',              labelKey: 'fields.employer',            type: 'labeled-code' },
      { key: 'country',               labelKey: 'fields.country',             type: 'labeled-code' },
      { key: 'pay_plan',              labelKey: 'fields.payPlan',             type: 'labeled-code' },
      { key: 'salary_scale',          labelKey: 'fields.salaryScale',         type: 'labeled-code' },
      { key: 'base_salary',           labelKey: 'fields.baseSalary',          type: 'text' },
      { key: 'salary_with_seniority', labelKey: 'fields.salaryWithSeniority', type: 'text' },
      { key: 'activity_rate',         labelKey: 'fields.activityRate',        type: 'text' },
      { key: 'currency',              labelKey: 'fields.currency',            type: 'labeled-code' },
    ],
  },

  {
    id: 'seniority',
    group: 'contract',
    column: 2,
    titleKey: 'sections.seniority',
    fields: [
      { key: 'seniority_months', labelKey: 'fields.seniorityMonths', type: 'text', fullWidth: true },
    ],
  },

  {
    id: 'leave',
    group: 'contract',
    column: 2,
    titleKey: 'sections.leave',
    fields: [
      { key: 'leave_type',              labelKey: 'fields.leaveType',            type: 'text' },
      { key: 'leave_accrual_per_month', labelKey: 'fields.leaveAccrualPerMonth', type: 'text' },
      { key: 'leave_balance_date',      labelKey: 'fields.leaveBalanceDate',     type: 'date' },
      { key: 'leave_balance',           labelKey: 'fields.leaveBalance',         type: 'text' },
    ],
  },

  {
    id: 'funding',
    group: 'contract',
    column: 2,
    titleKey: 'sections.funding',
    fields: [
      { key: 'accounting_code',    labelKey: 'fields.accountingCode',    type: 'labeled-code' },
      { key: 'third_party_code',   labelKey: 'fields.thirdPartyCode',    type: 'text' },
      { key: 'project',            labelKey: 'fields.project',           type: 'labeled-code' },
      { key: 'sub_project',        labelKey: 'fields.subProject',        type: 'labeled-code' },
      { key: 'axe_analytique',     labelKey: 'fields.axeAnalytique',     type: 'labeled-code' },
      { key: 'axe_analytique_2',   labelKey: 'fields.axeAnalytique2',    type: 'labeled-code' },
      { key: 'analytical_line',    labelKey: 'fields.analyticalLine',    type: 'labeled-code' },
      { key: 'analytical_line_2',  labelKey: 'fields.analyticalLine2',   type: 'labeled-code' },
      { key: 'funding_contract',   labelKey: 'fields.fundingContract',   type: 'labeled-code' },
      { key: 'funding_line',       labelKey: 'fields.fundingLine',       type: 'labeled-code' },
    ],
  },

  {
    id: 'manager',
    group: 'contract',
    column: 2,
    titleKey: 'sections.manager',
    fields: [
      // Responsable hiérarchique + fonctionnel côte à côte (grille 2 colonnes).
      { key: 'line_manager',       labelKey: 'fields.lineManager',       type: 'labeled-code' },
      { key: 'functional_manager', labelKey: 'fields.functionalManager', type: 'labeled-code' },
    ],
  },

  {
    id: 'contract_comment',
    group: 'contract',
    column: 2,
    stack: true,
    titleKey: 'sections.comment',
    fields: [
      { key: 'comment', labelKey: 'fields.comment', type: 'text', fullWidth: true },
    ],
  },
];
